import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./VerifyEmail.module.scss";
import { motion as Motion } from "framer-motion";

/* Simple appear animation */
const appear = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export default function VerifyEmail() {
  const t = useCallback((k, o) => i18n.t(k, o), []);
  const nav = useNavigate();
  const [search] = useSearchParams();
  const token = search.get("token");
  const emailParam = (search.get("email") || "").trim();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(emailParam);
  const [status, setStatus] = useState("idle"); // idle|verifying|verified|error

  // Derived
  const canResend = useMemo(() => !!email && email.includes("@"), [email]);

  // Actions
  const onResend = useCallback(async () => {
    if (!canResend) {
      toast.info(
        t("verifyEmail.enterEmail", {
          defaultValue: "Enter your email to resend verification.",
        }),
      );
      return;
    }
    try {
      setLoading(true);
      await api.post(
        "/v1/auth/verify/request",
        { email: email.toLowerCase() },
        { skipToast: true },
      );
      toast.success(
        t("verifyEmail.sentAgain", {
          defaultValue: "Verification email resent. Please check your inbox.",
        }),
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        t("verifyEmail.resendFailed", {
          defaultValue: "Could not resend verification email.",
        });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [canResend, email, t]);

  // Auto-verify when token is present
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!token) return;
      try {
        setStatus("verifying");
        const { data } = await api.get(`/v1/auth/verify`, {
          params: { token },
          skipToast: true,
        });
        if (!mounted) return;
        if (data?.ok) {
          setStatus("verified");
          toast.success(
            t("verifyEmail.verified", {
              defaultValue: "Email verified! You can log in now.",
            }),
          );
          setTimeout(() => nav("/login", { replace: true }), 700);
        } else {
          setStatus("error");
          toast.error(
            t("verifyEmail.invalidOrExpired", {
              defaultValue: "Invalid or expired verification link.",
            }),
          );
        }
      } catch (e) {
        if (!mounted) return;
        setStatus("error");
        const msg =
          e?.response?.data?.message ||
          t("verifyEmail.verifyFailed", {
            defaultValue: "Could not verify email.",
          });
        toast.error(msg);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [token, nav, t]);

  // Reusable panel used in both mobile & desktop sections
  const Panel = () => (
    <Motion.div
      className={`${cls.frame} ${cls["frame--primary"]}`}
      variants={appear}
      initial="hidden"
      animate="show"
    >
      <h1 className={`${cls.title} ${cls["is-mrs"]}`}>
        {t("verifyEmail.title", { defaultValue: "Verify your email" })}
      </h1>
      <p className={cls.subtitle}>
        {t("verifyEmail.subtitle", {
          defaultValue:
            "Click the verification link we sent to your inbox to activate your account.",
        })}
      </p>

      {token ? (
        <div className={cls.body}>
          {status === "verifying" && (
            <p className={cls.help}>
              {t("verifyEmail.verifying", {
                defaultValue: "Verifying your email…",
              })}
            </p>
          )}

          {status === "verified" && (
            <p className={cls.help}>
              {t("verifyEmail.redirectLogin", {
                defaultValue: "Verified! Redirecting to login…",
              })}
            </p>
          )}

          {status === "error" && (
            <>
              <p className={cls.errorText}>
                {t("verifyEmail.invalidOrExpired", {
                  defaultValue: "Invalid or expired verification link.",
                })}
              </p>

              <div className={cls.form}>
                <label className={cls.label}>
                  {t("verifyEmail.emailLabel", { defaultValue: "Email" })}
                </label>
                <input
                  className={cls.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("verifyEmail.emailPlaceholder", {
                    defaultValue: "you@example.com",
                  })}
                  autoComplete="email"
                />
                <button
                  type="button"
                  className={cls.submit}
                  onClick={onResend}
                  disabled={!canResend || loading}
                >
                  {loading
                    ? t("verifyEmail.resending", {
                        defaultValue: "Resending…",
                      })
                    : t("verifyEmail.resendBtn", {
                        defaultValue: "Resend verification",
                      })}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={cls.form}>
          <p className={cls.help} style={{ marginBottom: 12 }}>
            {t("verifyEmail.instructions", {
              email: email || "your email",
              defaultValue:
                "We sent a verification link to {{email}}. Didn’t receive it? Resend below.",
            })}
          </p>

          <label className={cls.label}>
            {t("verifyEmail.emailLabel", { defaultValue: "Email" })}
          </label>
          <input
            className={cls.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("verifyEmail.emailPlaceholder", {
              defaultValue: "you@example.com",
            })}
            autoComplete="email"
          />
          <button
            type="button"
            className={cls.submit}
            onClick={onResend}
            disabled={!canResend || loading}
          >
            {loading
              ? t("verifyEmail.resending", { defaultValue: "Resending…" })
              : t("verifyEmail.resendBtn", {
                  defaultValue: "Resend verification",
                })}
          </button>

          <div className={cls.altRow}>
            <span>
              {t("verifyEmail.haveAccount", {
                defaultValue: "Already verified?",
              })}
            </span>
            <Link to="/login" className={cls.link}>
              {t("verifyEmail.goLogin", { defaultValue: "Go to login" })}
            </Link>
          </div>
        </div>
      )}
    </Motion.div>
  );

  return (
    <main className={cls.home}>
      {/* Mobile / Portrait */}
      <section className="nu-mobile nu-bg--mobile">
        <div className={cls.stage}>
          <Panel />
        </div>
      </section>

      {/* Desktop / Landscape */}
      <section className="nu-desktop nu-bg--desktop">
        <div className={cls.stage}>
          <Panel />
        </div>
      </section>
    </main>
  );
}
